import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";

/**
 * usePremiumStatus — canonical source of truth for the user's plan state.
 *
 * Consolidates the two backend surfaces (/auth/me and /me/access) into a
 * single derived object so every FE component displays a consistent status
 * regardless of which context updated first. Fixes the historical race
 * where the profile badge briefly showed "Trial" after a successful
 * payment while /me/access was still resolving.
 *
 * Precedence rules (Premium ALWAYS wins over Trial):
 *   1. `access.is_premium` (server-derived, checks premium_until vs now) →
 *      tier="premium". Trumps every other signal.
 *   2. `user.is_premium` (raw DB flag) — used as a fallback ONLY if
 *      `access` hasn't loaded yet. Prevents a "flicker" on first paint.
 *   3. `access.in_trial` → tier="trial". Only shown when premium is NOT
 *      active — never both.
 *   4. Otherwise → tier="free".
 *
 * Returns:
 *   {
 *     tier: "premium" | "trial" | "free",
 *     isPremium: boolean,   // active paid OR trial? — false if free
 *     isPaid: boolean,       // strictly premium (not trial)
 *     isTrial: boolean,      // in welcome trial, NOT paid
 *     effective: boolean,    // isPaid || isTrial (unlocks premium features)
 *     label: string,         // human copy — "Syllox Premium", "Trial member", "Free plan"
 *     planKind: string|null, // "monthly" | "yearly" | null
 *     daysLeft: number|null, // premium_days_left when paid; trial_days_left when trial
 *     expiresAt: string|null,// ISO — premium_until (paid) or trial_ends_at (trial)
 *   }
 */
export function usePremiumStatus() {
  const { user } = useAuth();
  const { access } = useAccess();

  return useMemo(() => {
    // Prefer server-derived state. Fall back to raw user.is_premium only if
    // access hasn't loaded yet (very first paint after login).
    const paidFromAccess = !!access?.is_premium;
    const paidFromUser = !!user?.is_premium;
    const isPaid = paidFromAccess || (access === null && paidFromUser);

    // Trial is DERIVED only. Premium always wins — never show trial when
    // the user has active paid premium, even if the trial window is still
    // open (paid within trial is a valid state).
    const isTrial = !!access?.in_trial && !isPaid;

    const tier = isPaid ? "premium" : isTrial ? "trial" : "free";

    const daysLeft = isPaid
      ? (Number.isFinite(access?.premium_days_left) ? access.premium_days_left : null)
      : isTrial
        ? (Number.isFinite(access?.trial_days_left) ? access.trial_days_left : null)
        : null;

    const expiresAt = isPaid
      ? (access?.premium_until || user?.premium_until || null)
      : isTrial
        ? (access?.trial_ends_at || null)
        : null;

    const planKind = isPaid ? (access?.premium_plan_kind || user?.premium_plan_kind || null) : null;

    let label;
    if (isPaid) label = "Syllox Premium";
    else if (isTrial) label = "Trial member";
    else label = "Free plan";

    return {
      tier,
      isPremium: isPaid || isTrial,
      isPaid,
      isTrial,
      effective: isPaid || isTrial,
      label,
      planKind,
      daysLeft,
      expiresAt,
    };
  }, [user, access]);
}

/**
 * Format an ISO datestring like "2026-08-24T04:50:01+00:00" as
 * "Aug 24, 2026". Never throws — malformed input yields an empty string.
 */
export function formatExpiryDate(iso) {
  if (!iso || typeof iso !== "string") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
